/**
 * Where a rewrite goes, worked out on the text itself (ADR-0025).
 *
 * Offsets are UTF-16 code units, the same units a textarea's selectionStart
 * and selectionEnd use — so a selection read from the editor can be used here
 * directly.
 */

export interface Range {
  start: number;
  end: number;
}

export type Placement = "replace" | "after";

/**
 * place puts a version into the text where the selection was, and returns the
 * new text with the range the version now occupies — so the editor can select
 * it, and a second rewrite of the same passage starts from what was placed.
 *
 * "after" adds the version as its own paragraph when the selection ends one,
 * and after a space when it ends mid-paragraph.
 */
export function place(
  body: string,
  selection: Range,
  text: string,
  how: Placement,
): { body: string; selection: Range } {
  if (how === "replace") {
    return {
      body: body.slice(0, selection.start) + text + body.slice(selection.end),
      selection: { start: selection.start, end: selection.start + text.length },
    };
  }
  const rest = body.slice(selection.end);
  const joiner = rest === "" || rest.startsWith("\n") ? "\n\n" : " ";
  const start = selection.end + joiner.length;
  return {
    body: body.slice(0, selection.end) + joiner + text + rest,
    selection: { start, end: start + text.length },
  };
}

/** How much text either side of a selection travels with a rewrite. */
export const CONTEXT_CHARS = 1500;

/** contextOf is the text around a selection, nearest part first to be kept. */
export function contextOf(body: string, selection: Range): { before: string; after: string } {
  return {
    before: body.slice(Math.max(0, selection.start - CONTEXT_CHARS), selection.start),
    after: body.slice(selection.end, selection.end + CONTEXT_CHARS),
  };
}
